/**
 * @author    Odessa Team (odessateam@ab-soft.net)
 * @category  Ringcentral
 * @copyright Copyright (c) 2012-2019, RingCentral, Inc (http://www.ringcentral.com)
 */
import {StatusedResponse} from "@signup/base/Response";
import TrustSiteApiDefaultBase from "@dashboard/ajax/TrustSiteApiDefaultBase";
import RestClient from "@signup/ajax/RestClient";
import AxiosAjaxClient from "@signup/ajax/AxiosAjaxClient";
import type {ServiceReport} from "@entity/ServiceReport";
import type {Banner} from "@entity/Banner";
import type {UptimeReport} from "@entity/UptimeReport";

/**
 * Implements Trust Site Service API client.
 */
export default class TrustSiteApiService extends TrustSiteApiDefaultBase
{
    /** Login cookie name. */
    private static readonly authorizedCookie: string = "authorized";

    /**
     * Returns service report.
     *
     * @returns Request result
     */
    public status(): Promise<unknown>
    {
        return (async () => {
            const response: StatusedResponse = await this.rest.get(`service/status/`);

            return response.result;
        })();
    }

    /**
     * Returns service report.
     *
     * @returns Request result
     */
    public statusStatic(): Promise<ServiceReport>
    {
        return (async () => {
            const staticRest = new RestClient<ServiceReport>("", new AxiosAjaxClient<ServiceReport>({ errorMode: "structured" }));
            if (TrustSiteApiService.isLoggedIn()) {
                return await staticRest.get(`/api/1.0/user/incidents/`);
            } else {
                return await staticRest.get(`/status.json`);
            }
        })();
    }

    /**
     * Returns service report.
     *
     * @returns Request result
     */
    public statusBanner(): Promise<Banner[][]> {
        return (async () => {
            const staticRest = new RestClient<Banner[][]>("", new AxiosAjaxClient<Banner[][]>({ errorMode: "structured" }));
            if (TrustSiteApiService.isLoggedIn()) {
                return await staticRest.get(`/api/1.0/banner/user/`);
            }

            return await staticRest.get(`/banner.json`);
        })();
    }

    /** Returns the same static uptime report used by the legacy RC dashboard. */
    public uptimeHistoryStatic(): Promise<UptimeReport> {
        return new RestClient<UptimeReport>().get("/uptime.json");
    }

    /**
     * Returns cookie value by name or false.
     */
    public static getCookieByName(cookieName: string): string | boolean {
        const match = document.cookie.match(new RegExp("(^| )" + cookieName + "=([^;]+)"));
        if (match) {
            return match[2];
        }
        else{
            return false;
        }
    }

    /**
     * Is user Logged in.
     */
    public static isLoggedIn(): boolean {

        return this.getCookieByName(this.authorizedCookie) === "1";
    }

    /**
     * Returns chatbot data.
     *
     * @returns Request result
     */
    public getChatConfig(): Promise<any> {
        return (async () => {
            const staticRest = new RestClient();
            return await staticRest.get(`/api/1.0/chatbot/config/`);
        })();
    }

}
